import Cache from '@powerednow/complexData/cache';
import { AssociationConfig } from '@powerednow/complexData/complexData';
import { AuthManager } from './AuthManager';

declare let Ext: any;
declare let handleClientError: any;

export default class ExtCache extends Cache {
    static getIgnoredAssociationConditionKeys(association: AssociationConfig<any, any>): string[] {
        return (association.instance as any).serverOnlyFieldNames;
    }

    private observedModels = {};

    private isModelObserved(modelName) {
        return this.observedModels[modelName] || false;
    }

    private handleNewStoreRecords(modelName, model, records) {
        try {
            records.forEach(record => {
                const { data } = record;
                if (Object.getOwnPropertySymbols(data).length === 0) {
                    this.updateOnAdd(modelName, { ...data, company_id: AuthManager.getCompany().data.id });
                }
            });
        } catch (err) {
            handleClientError(err);
        }
    }

    protected notifyOnNewModel(modelName: string): void {
        if (this.isModelObserved(modelName)) {
            return;
        }

        this.observedModels[modelName] = true;

        const store = Ext.getStore(modelName) || Ext.getStore(modelName.toLowerCase());

        if (!store) {
            //
            // this is a valid case when there is an association what references a table
            // what is not used in the app.
            // But just to be sure we log a warning here to help detecting cases
            // when it is a result of a coding error.
            //
            console.warn(`No store found: ${modelName}`);
            return;
        }

        /**
         * This is here because PN-8621. It is almost the same as the event handler on 'update' below, but this one
         * will update the complexData case in synchronous manner.
         */
        store.on('beforesync', operations => {
            const recordsToUpdate = operations && operations.update;
            if (recordsToUpdate) {
                recordsToUpdate.forEach(record => {
                    this.updateData(modelName, record.getData());
                });
            }
        });

        /**
         * This left here to be sure nothing is broken while fixing PN-8261. My biggest fear is that we do something
         * like that:
         * `
         * --NO AWAIT-- Ext.getStore('Document').syncWithListenerAsync();
         * [...]
         * Ext.getStore('Document').data.items[0].set('confirmed',true);
         * ...
         * if (Ext.getStore('Document').needSync) {
         *      await Ext.getStore('Document').syncWithListenerAsync();
         * } else {
         *     // There won't be error, sync will see the changes, but there will be a small window when
         *     // ext record and complexdata is not in sync.
         * }
         *
         */
        store.on('update', (model, record, operation, modifiedFieldNames, details, eOpts) => {
            if ([Ext.data.Model.COMMIT].includes(operation)) {
                try {
                    this.updateData(modelName, { ...record.getData(), company_id: AuthManager.getCompany().data.id });
                } catch (err) {
                    handleClientError(err);
                }
            }
        });

        store.on('add', this.handleNewStoreRecords.bind(this, modelName));

        store.data.on('remove', async (model, records) => {
            try {
                records.items.forEach(({ data }) => {
                    if (Object.getOwnPropertySymbols(data).length === 0) {
                        this.updateOnRemove(modelName, data).catch(handleClientError);
                    }
                });
            } catch (err) {
                handleClientError(err);
            }
        });
    }
}
